import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import CloseIcon from '@mui/icons-material/Close'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import { message, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import React, { useCallback, useState } from 'react'

import { Link, useNavigate } from 'react-router-dom'
import CircularLoading from '../../../components/CircularProgress'
import OrderTag from '../../../components/OrderTag'
import OverdueTag from '../../../components/OrderTag/overdue'
import PrintedTag from '../../../components/OrderTag/printed'
import Pagination from '../../../components/Pagination'
import ProductItemDetailTag from '../../../components/ProductItemDetailTag'
import TrackingProviderImage from '../../../components/TrackingProviderImage'
import {
  ONLY_DATE_FORMAT,
  ONLY_DATE_TIME_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../constants/global'
import PageUrls from '../../../constants/page-urls'
import { useUser } from '../../../hooks/useUser'
import {
  EOrderStatus,
  EOrderType,
  IOrder,
  IOrderItem,
  OrderRecipient,
  OrderShippingProvider,
} from '../../../types/order'
import { IFilterMapped } from './TrackingFilter'
import { FiCopy, FiRefreshCcw } from 'react-icons/fi'
import TrackingTimelineModal from '../../../components/Modal/TrackingTimelineModal'
import FacebookTag from '../../../components/OrderTag/facebookTag'
import DistrubuteChannelTag from '../../../components/DistrubuteChannelTag'
import TelesaleTag from '../../../components/OrderTag/telesale'
import CrmTag from '../../../components/OrderTag/crmTag'

interface ITrackingTableProps {
  selectedData: IOrder[]
  setSelectedData: (orders: IOrder[]) => void
  isLoading: boolean
  data: IOrder[]
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  onRefresh?: () => void
  total: number
}

const TrackingTable = (props: ITrackingTableProps) => {
  const { merchant } = useUser()
  const showSocial = !!merchant?.config?.display?.social

  const {
    selectedData,
    setSelectedData,
    isLoading,
    data,
    filter,
    onFilterChange,
    handlePageChange,
    onRefresh,
    total,
  } = props

  const navigate = useNavigate()

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const orderObjects = data?.reduce<{ [key: string]: IOrder }>((acc, cur) => {
      acc[cur.objectId] = cur
      return acc
    }, {})
    const orders = keys.map((key: string) => {
      return orderObjects[key]
    })
    setSelectedData(orders)
  }

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [order, setOrder] = useState<IOrder | null>(null)
  const [slipUrl, setSlipUrl] = useState<string | null>(null)

  const handleShowTracking = useCallback((order: IOrder) => {
    setIsOpen(true)
    setOrder(order)
  }, [])

  const handleCopyText = useCallback((text: string) => {
    navigator.clipboard.writeText(text ?? '')
    message.success(`คัดลอกแล้ว: ${text ?? ''}`)
  }, [])

  const columns: TableColumnsType<IOrder & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        width: 80,
        align: 'center' as const,
        render: (numberRow: number) => (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberRow}
          </Typography>
        ),
      },
      {
        title: 'สถานะ',
        dataIndex: 'status',
        width: 150,
        key: 'status',
        align: 'center' as const,
        render: (status: string, order: IOrder) => {
          const lastTracking =
            order.trackings?.[order.trackings?.length - 1] ??
            ({} as { trackingDate: string })
          return (
            <>
              {order.trackingCode && order.trackingProvider && (
                <Box mb={1}>
                  <TrackingProviderImage
                    trackingProvider={
                      order.trackingProvider as OrderShippingProvider
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.5}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      textAlign={'center'}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleShowTracking(order)}
                    >
                      {order.trackingCode}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyText(order?.trackingCode ?? '')}
                    >
                      <FiCopy size={12} />
                    </IconButton>
                  </Box>
                  <Typography fontSize={10}>
                    {order?.trackingUpdatedAt
                      ? dayjs(order?.trackingUpdatedAt).format(
                          ONLY_DATE_TIME_FORMAT,
                        )
                      : lastTracking?.trackingDate
                        ? dayjs(lastTracking.trackingDate).format(
                            ONLY_DATE_TIME_FORMAT,
                          )
                        : ''}
                  </Typography>
                </Box>
              )}
              <OrderTag status={status as EOrderStatus} />
            </>
          )
        },
      },
      {
        title: 'เลขออเดอร์',
        dataIndex: 'code',
        key: 'code',
        align: 'center' as const,
        render: (code: string, order: IOrder) => (
          <>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={0.5}
            >
              <Typography
                variant="subtitle1"
                color="text.primary"
                component={Link}
                to={`${PageUrls.Orders.Detail}/${order.objectId}`}
                sx={{ cursor: 'pointer' }}
              >
                {code}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopyText(`${order?.code ?? ''}`)}
              >
                <FiCopy size={12} />
              </IconButton>
            </Box>
            <Typography fontSize={11}>
              {order.orderDate
                ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                : ''}
            </Typography>
          </>
        ),
      },
      {
        title: 'ราคา',
        dataIndex: 'total',
        key: 'total',
        align: 'center' as const,
        render: (total: string, order: IOrder) => (
          <Grid
            container
            flexDirection={'column'}
            textAlign={'center'}
            minWidth={120}
          >
            <Grid item>
              <Typography
                variant="h5"
                color={
                  order?.paymentMethod === EOrderType.COD ? 'red' : 'green'
                }
              >
                {total.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
                sx={{ marginTop: 1 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {order?.paymentMethod === EOrderType.COD
                    ? 'เก็บเงินปลายทาง'
                    : 'โอนผ่านธนาคาร'}
                </Typography>
                {order?.paymentMethod !== EOrderType.COD &&
                  order?.paymentUrl && (
                    <IconButton
                      size="small"
                      title="ดูสลิป"
                      onClick={() => setSlipUrl(order.paymentUrl ?? null)}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
              </Box>
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {order.items.length} รายการ/{order.quantity} ชิ้น
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'จัดส่ง',
        dataIndex: 'recipient',
        key: 'recipient',
        align: 'center' as const,
        render: (recipient: OrderRecipient) => (
          <Grid
            container
            flexDirection={'column'}
            minWidth={200}
            textAlign={'left'}
          >
            <Grid item>
              <Typography variant="subtitle1">{recipient?.name}</Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {recipient?.address} {recipient?.subDistrict}{' '}
                {recipient?.district} {recipient?.province} {recipient?.zipcode}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1">{recipient?.phone}</Typography>
            </Grid>
            {showSocial && recipient?.social && (
              <Grid item>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  title="โซเชียลของลูกค้า"
                >
                  <AlternateEmailIcon
                    sx={{ fontSize: 14 }}
                    color="disabled"
                  />
                  <Typography variant="subtitle2" color="text.secondary">
                    {recipient.social}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        title: 'สินค้า',
        dataIndex: 'items',
        key: 'items',
        align: 'center' as const,
        render: (items: IOrderItem[]) => (
          <Box minWidth={150}>
            {items?.map((item: IOrderItem, index: number) => {
              return (
                <ProductItemDetailTag
                  key={index}
                  item={{
                    name: item.productName,
                    quantity: item.quantity,
                    isCollection: item.isCollection,
                    url: item.imageUrl,
                    commissionName: item.commissionUsername,
                    color: item.meta?.color,
                    size: item.meta?.size,
                  }}
                />
              )
            })}
          </Box>
        ),
      },
      {
        title: 'วันที่เพิ่ม',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        align: 'center' as const,
        render: (createdAt: string) => (
          <Grid container flexDirection={'column'} textAlign={'center'}>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="caption" color="text.secondary">
                {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'หมายเหตุ',
        dataIndex: 'note',
        key: 'note',
        align: 'center' as const,
        render: (note: string, order: IOrder) => {
          return (
            <>
              <DistrubuteChannelTag distribution={order.distribution} />
              <TelesaleTag order={order} />
              <CrmTag order={order} />
              <OverdueTag order={order} />
              <PrintedTag order={order} />
              <FacebookTag order={order} />
              {note ? (
                <Typography variant="subtitle2" textAlign={'left'}>
                  {note}
                </Typography>
              ) : (
                <></>
              )}
            </>
          )
        },
      },
    ]

  const rowSelection = {
    selectedRowKeys: selectedData.map((order: IOrder) => order.objectId),
    onChange: onSelectChange,
  }

  return (
    <>
      <TrackingTimelineModal
        order={order}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={`ประวัติการจัดส่ง ${order?.trackingCode ?? ''}`}
      />
      <Dialog
        open={!!slipUrl}
        onClose={() => setSlipUrl(null)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            position: 'relative',
            m: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setSlipUrl(null)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          component="img"
          src={slipUrl ?? ''}
          alt="สลิปการโอนเงิน"
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '85vh',
            objectFit: 'contain',
          }}
        />
      </Dialog>
      <CircularLoading isLoading={isLoading} />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        rowSelection={rowSelection}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((order: IOrder, index: number) => {
          return {
            ...order,
            key: order.objectId,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
        locale={{
          emptyText: (
            <Box py={4} display="flex" flexDirection="column" alignItems="center" gap={1.5}>
              <Typography variant="subtitle2" color="text.secondary">
                ไม่พบข้อมูล
              </Typography>
              {onRefresh && (
                <Button
                  variant="outlined"
                  size="medium"
                  color="primary"
                  startIcon={<FiRefreshCcw />}
                  onClick={onRefresh}
                  disabled={isLoading}
                >
                  รีเฟรช
                </Button>
              )}
            </Box>
          ),
        }}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default TrackingTable
